import { Link } from 'react-router-dom';
import { useState } from 'react';
import { trackLead } from '../../../utils/metaPixel';

export default function Hero() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: 'Soin visage complet',
    lieu: 'Cabinet à Mons',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('service', formData.service);
      formBody.append('lieu', formData.lieu);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d4hibl2jg2jl50j1s5g0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: 'Soin visage complet', lieu: 'Cabinet à Mons', message: '' });
        trackLead({ content_name: 'Réservation Soin Plasma Froid - Soins Esthétiques' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section className="relative bg-white">
      {/* Navigation */}
      <nav className="py-6 px-6 lg:px-12 border-b border-rose-100">
        <div className="container mx-auto flex justify-between items-center">
          <Link to="/" className="text-2xl font-light text-gray-900">
            Peau<span className="font-semibold text-rose-500">Glow</span>
          </Link>
          <div className="flex items-center gap-6">
            <Link to="/boutique" className="text-gray-600 hover:text-rose-500 transition-colors cursor-pointer whitespace-nowrap">
              Boutique
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Content */}
      <div className="container mx-auto px-6 lg:px-12 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <h1 className="text-5xl lg:text-6xl font-light text-gray-900 mb-6 leading-tight">
                Soins <span className="font-semibold text-rose-500">Plasma Froid</span>
              </h1>
              <p className="text-2xl text-gray-600 mb-4">Mons & Domicile en Belgique</p>
              <p className="text-lg text-gray-600 mb-8">
                Technologie avancée pour une peau visiblement transformée. Résultats durables, soin non-invasif.
              </p>

              {/* Mini benefits */}
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-rose-500 text-xl mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Résultats visibles</div>
                    <div className="text-sm text-gray-600">Amélioration de l'apparence dès la première séance</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-rose-500 text-xl mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">Non-invasif</div>
                    <div className="text-sm text-gray-600">Aucune injection, technologie douce</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-rose-500 text-xl mt-1"></i>
                  <div>
                    <div className="font-semibold text-gray-900">À domicile ou en cabinet</div>
                    <div className="text-sm text-gray-600">Flexibilité totale selon vos besoins</div>
                  </div>
                </div>
              </div>

              {/* Image for mobile */}
              <div className="lg:hidden mb-8">
                <img 
                  src="https://public.readdy.ai/ai/img_res/7a4b52ed094a8fa060de754e9be9a5f5.jpg"
                  alt="Soin Perlée Froid"
                  className="w-full h-64 object-cover object-top rounded-2xl shadow-xl"
                />
              </div>
            </div>

            {/* Right: Booking Form */}
            <div className="bg-gradient-to-br from-rose-50 to-blush-50 rounded-3xl p-8 border border-rose-100 shadow-xl">
              <h3 className="text-2xl font-semibold text-gray-900 mb-6">Réservez maintenant</h3>
              
              <form id="booking_soins" data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all bg-white"
                    placeholder="Votre nom"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all bg-white"
                    placeholder="votre@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all bg-white"
                    placeholder="06 12 34 56 78"
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    Type de soin *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all bg-white cursor-pointer"
                  >
                    <option value="Soin visage complet">Soin visage complet</option>
                    <option value="Soin contour des yeux">Soin contour des yeux</option>
                    <option value="Soin cou et décolleté">Soin cou et décolleté</option>
                    <option value="Soin mains">Soin mains</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="lieu" className="block text-sm font-medium text-gray-700 mb-2">
                    Lieu *
                  </label>
                  <select
                    id="lieu"
                    name="lieu"
                    value={formData.lieu}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all bg-white cursor-pointer"
                  >
                    <option value="Cabinet à Mons">Cabinet à Mons</option>
                    <option value="À domicile">À domicile</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-rose-200 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent transition-all resize-none bg-white"
                    placeholder="Précisions, préférences..."
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 caractères</p>
                </div>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    ✓ Demande envoyée ! Nous vous contactons sous 24h.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    Erreur. Veuillez réessayer.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-rose-500 text-white py-4 px-8 rounded-lg font-semibold hover:bg-rose-600 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Envoi...' : 'Réserver mon soin'}
                </button>
              </form>
            </div>
          </div>

          {/* Image for desktop */}
          <div className="hidden lg:block mt-12">
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20cold%20plasma%20facial%20treatment%20in%20elegant%20modern%20spa%20with%20soft%20pink%20lighting%20advanced%20aesthetic%20technology%20device%20serene%20beauty%20clinic%20atmosphere%20clean%20minimalist%20medical%20aesthetic%20environment%20professional%20skincare&width=1200&height=400&seq=soins-hero-main-002&orientation=landscape"
              alt="Soin Perlée Froid"
              className="w-full h-96 object-cover object-top rounded-3xl shadow-2xl"
            />
          </div>
        </div>
      </div>

      {/* Sticky CTA */}
      <div className="fixed bottom-6 right-6 z-50 lg:hidden">
        <a 
          href="#booking_soins"
          className="flex items-center gap-2 px-6 py-4 bg-rose-500 text-white rounded-full font-semibold shadow-2xl hover:bg-rose-600 transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-calendar-line"></i>
          Réserver
        </a>
      </div>
    </section>
  );
}
